import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Which days are on screen, from the grid's own scroll position, and the one
 * place that moves it.
 *
 * The scroll handler does no work of its own: it schedules one animation
 * frame, and that frame reads `scrollLeft` once and updates state only when
 * the first visible day or the number of visible days actually changed. So a
 * scroll across a day costs one render and a scroll within one costs none
 * (CAP-1416's "scroll work once per frame"). Whoever renders the grid decides
 * from `firstVisibleDay` which windows to read and how far the grid runs.
 *
 * Days are day numbers (`windows.ts`), not positions on the axis, so the grid
 * may gain or lose days on its left (a window read into the past, a far window
 * dropped) without the view moving: when the axis's first day changes, the
 * same layout effect that commits the new track moves `scrollLeft` by the
 * width added or removed, before the browser paints.
 *
 * The grid renders only `renderStart`..`renderEnd` (axis indices: the visible
 * days plus `overscan` either side); its track keeps the axis's full width, so
 * the scrollbar stays honest. Until the grid has a width (a first paint, a
 * test without layout) every day is rendered.
 */
export interface VisibleDays {
  /** The day number at the left edge: the opening day until the grid is measured. */
  firstVisibleDay: number
  /** How many days fit beside the property column, or 0 before the grid is measured. */
  visibleCount: number
  /** The first and last axis index (inclusive) to put in the DOM. */
  renderStart: number
  renderEnd: number
  /**
   * Brings `day` to the left edge: smoothly when asked (and the reader has not
   * asked for reduced motion), otherwise at once. Either way it starts after
   * the next commit, so the caller can first give the grid every day the move
   * passes (see `PortfolioCalendar`'s glide): a smooth scroll is cancelled by
   * any change to `scrollLeft` on its way, such as a window added on the left.
   * Returns whether it glides.
   */
  scrollToDay: (day: number, options?: { smooth?: boolean }) => boolean
}

interface UseVisibleDaysOptions {
  dayWidth: number
  /** The sticky property column's width: it covers the left of the scrolled area. */
  asideWidth: number
  /** The day number of the axis's first day. */
  axisStart: number
  dayCount: number
  /** The day to bring to the left edge on first paint. */
  initialDay: number
  overscan?: number
  /**
   * Called on every scroll event, before the frame is scheduled: for what must
   * go the moment the grid moves, such as a tooltip whose item moves away
   * without a pointer leave (CAP-1418). Keep it cheap; it may change identity.
   */
  onScroll?: () => void
}

export const VISIBLE_DAYS_OVERSCAN = 10

interface Anchor {
  first: number
  count: number
}

// The view is moved through these, outside the hook: the scroll position is
// the browser's state, not React's.
function placeAt(element: HTMLElement, left: number) {
  element.scrollLeft = left
}

function moveBy(element: HTMLElement, delta: number) {
  element.scrollLeft += delta
}

function glideTo(element: HTMLElement, left: number) {
  if (typeof element.scrollTo === 'function') element.scrollTo({ left, behavior: 'smooth' })
  else element.scrollLeft = left
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function useVisibleDays(
  element: HTMLElement | null,
  {
    dayWidth,
    asideWidth,
    axisStart,
    dayCount,
    initialDay,
    overscan = VISIBLE_DAYS_OVERSCAN,
    onScroll: onScrollOption,
  }: UseVisibleDaysOptions
): VisibleDays {
  const [anchor, setAnchor] = useState<Anchor>(() => ({ first: initialDay, count: 0 }))
  // A jump far away re-renders with the anchor already at the target, so the
  // grid is given the days around it; this counter makes sure the layout
  // effect then runs, even when the grid's days did not change.
  const [jumps, setJumps] = useState(0)

  // What the frame callbacks read: the latest committed values, kept outside
  // state so the scroll listener is attached once, not once per axis.
  const anchorRef = useRef(anchor)
  const axisRef = useRef({ axisStart, dayWidth })
  const targetRef = useRef<number | null>(null)
  const glideRef = useRef<number | null>(null)
  const placedRef = useRef<{
    element: HTMLElement
    axisStart: number
    dayWidth: number
  } | null>(null)
  const frameRef = useRef(0)
  const onScrollRef = useRef(onScrollOption)

  useLayoutEffect(() => {
    anchorRef.current = anchor
  }, [anchor])

  useLayoutEffect(() => {
    onScrollRef.current = onScrollOption
  }, [onScrollOption])

  const measure = useCallback(() => {
    if (!element || element.clientWidth === 0) return
    const { axisStart: start, dayWidth: width } = axisRef.current
    const first = start + Math.max(0, Math.floor(element.scrollLeft / width))
    const count = Math.max(1, Math.ceil((element.clientWidth - asideWidth) / width))
    const last = anchorRef.current
    // Compared here rather than in a state updater, which React may still
    // render once to discover is a no-op.
    if (last.first === first && last.count === count) return
    const next = { first, count }
    anchorRef.current = next
    setAnchor(next)
  }, [element, asideWidth])

  // Put the view where it belongs whenever the track changes under it.
  useLayoutEffect(() => {
    const placed = placedRef.current
    axisRef.current = { axisStart, dayWidth }
    if (!element) return
    const target = targetRef.current
    // A jump whose day the grid has not been given yet waits for it: placing
    // it now would only be clamped to the end of the track.
    if (target !== null && (target < axisStart || target >= axisStart + dayCount)) return
    placedRef.current = { element, axisStart, dayWidth }
    targetRef.current = null
    if (target !== null || !placed || placed.element !== element) {
      // A jump, or the grid's first paint: the day asked for at the left edge.
      placeAt(element, ((target ?? anchorRef.current.first) - axisStart) * dayWidth)
    } else if (placed.dayWidth !== dayWidth) {
      // Across the phone breakpoint: the same day stays at the left edge.
      placeAt(element, (anchorRef.current.first - axisStart) * dayWidth)
    } else if (placed.axisStart !== axisStart) {
      // Days added (or dropped) on the left: move by exactly their width, so
      // what is on screen stays still, to the pixel.
      moveBy(element, (placed.axisStart - axisStart) * dayWidth)
    }
    const glide = glideRef.current
    glideRef.current = null
    if (glide !== null) glideTo(element, (glide - axisStart) * dayWidth)
    // Read where that left the view: a new day width changes how many days
    // fit, and a placement the browser clamped fires no scroll of its own.
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0
        measure()
      })
    }
  }, [element, axisStart, dayCount, dayWidth, jumps, measure])

  useEffect(() => {
    if (!element) return undefined
    const onScroll = () => {
      onScrollRef.current?.()
      if (frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0
        measure()
      })
    }
    element.addEventListener('scroll', onScroll, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(element)
    measure()
    return () => {
      element.removeEventListener('scroll', onScroll)
      observer?.disconnect()
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
      frameRef.current = 0
    }
  }, [element, measure])

  const scrollToDay = useCallback((day: number, { smooth = false }: { smooth?: boolean } = {}) => {
    const glide = smooth && !prefersReducedMotion()
    if (glide) {
      glideRef.current = day
    } else {
      // At once: the anchor moves first, so the grid is given the days around
      // the target, and the layout effect puts the target at the left edge.
      targetRef.current = day
      const next = { first: day, count: anchorRef.current.count }
      anchorRef.current = next
      setAnchor(next)
    }
    setJumps(count => count + 1)
    return glide
  }, [])

  const offset = anchor.first - axisStart
  if (anchor.count === 0) {
    return {
      firstVisibleDay: anchor.first,
      visibleCount: 0,
      renderStart: 0,
      renderEnd: dayCount - 1,
      scrollToDay,
    }
  }
  return {
    firstVisibleDay: anchor.first,
    visibleCount: anchor.count,
    renderStart: Math.max(0, offset - overscan),
    renderEnd: Math.min(dayCount - 1, offset + anchor.count + overscan),
    scrollToDay,
  }
}
